// src/pages/Wallet.jsx
// Customer-facing prepaid wallet: view balance, top up via PayNow QR +
// screenshot upload for admin verification. Visually mirrors the Points &
// Rewards page (LoyaltyProgram.jsx) — same container, hero card, section,
// and history-list conventions from loyalty.css.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import {
  getWalletTopupPackages,
  subscribeToCustomer,
  getPendingTopupForCustomer,
  createTopupTransactionWithProof,
  getWalletTransactionHistory
} from '../api/walletService';
import WalletTopupQRModal from '../components/customer/WalletTopupQRModal';
import '../styles/loyalty.css';
import '../styles/wallet.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small': return '14px';
    case 'large': return '18px';
    default: return '16px';
  }
};

function formatCurrency(amount) {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
}

function formatDate(date) {
  if (!date) return '';
  const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const WalletIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 7H5a2 2 0 0 1 0-4h13a1 1 0 0 1 1 1v3z" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-3" />
    <path d="M21 12h-4a1.5 1.5 0 0 0 0 4h4v-4z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

function Wallet() {
  const { salonId } = useParams();
  const location = useLocation();
  const { currentCustomer } = useCustomerAuth();

  const [balance, setBalance] = useState(0);
  const [packages, setPackages] = useState([]);
  const [pendingTopup, setPendingTopup] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalGenerating, setModalGenerating] = useState(false);
  const [qrState, setQrState] = useState(null); // { qrCodeData, qrCodeUrl, fallback, isDynamic, amount, reference }
  const [startingAmount, setStartingAmount] = useState(null);

  const balanceRef = useRef(null);
  const topupRef = useRef(null);

  const customerId = currentCustomer ? `${currentCustomer.email}_${salonId}` : null;

  const showMessage = (message, isError = false) => {
    if (isError) { setError(message); setSuccessMessage(''); }
    else { setSuccessMessage(message); setError(''); }
    setTimeout(() => { setError(''); setSuccessMessage(''); }, 4000);
  };

  // Scroll to section when navigated from CustomerNavigation sub-tabs
  useEffect(() => {
    const section = location.state?.section;
    if (!section || loading) return;
    const refMap = { balance: balanceRef, topup: topupRef };
    const target = refMap[section];
    if (target?.current) {
      setTimeout(() => {
        const headerBar = document.querySelector('.customer-header-bar');
        const tabBar = document.querySelector('.customer-top-tab-bar');
        const offset = (headerBar?.offsetHeight || 0) + (tabBar?.offsetHeight || 0);
        const top = target.current.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 150);
    }
  }, [location.state?.section, loading]);

  // Salon branding — same as LoyaltyProgram
  useEffect(() => {
    const loadSalonTypography = async () => {
      try {
        const salonConfig = await getSalonConfig(salonId);
        if (salonConfig?.branding) {
          const root = document.documentElement;
          if (salonConfig.branding.colors) {
            root.style.setProperty('--salon-primary', salonConfig.branding.colors.primary || '#5b21b6');
            root.style.setProperty('--salon-secondary', salonConfig.branding.colors.secondary || '#ddd6fe');
            root.style.setProperty('--salon-accent', salonConfig.branding.colors.accent || '#F6F6F6');
            root.style.setProperty('--salon-background', salonConfig.branding.colors.background || '#FFFFFF');
            root.style.setProperty('--salon-text', salonConfig.branding.colors.text || '#333333');
          }
          if (salonConfig.branding.typography) {
            root.style.setProperty('--salon-heading-font', salonConfig.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
            root.style.setProperty('--salon-body-font', salonConfig.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
            root.style.setProperty('--salon-font-size', getFontSizeValue(salonConfig.branding.typography.fontSize));
          }
        }
      } catch (err) {
        console.error('Error loading salon typography:', err);
      }
    };
    if (salonId) loadSalonTypography();
  }, [salonId]);

  // Live balance subscription
  useEffect(() => {
    if (!customerId) return;
    const unsub = subscribeToCustomer(customerId, (data) => {
      setBalance(data?.metrics?.walletBalance || 0);
    });
    return unsub;
  }, [customerId]);

  // Admin-configured cash packages (pay/credit pairs), falls back to 1:1 defaults
  useEffect(() => {
    if (!salonId) return;
    getWalletTopupPackages(salonId)
      .then(setPackages)
      .catch(err => console.error('Error loading cash packages:', err));
  }, [salonId]);

  const refreshPendingAndHistory = useCallback(async () => {
    if (!currentCustomer || !salonId) { setLoading(false); return; }
    try {
      const [pending, hist] = await Promise.all([
        getPendingTopupForCustomer(currentCustomer.email, salonId),
        getWalletTransactionHistory(currentCustomer.email, salonId)
      ]);
      setPendingTopup(pending);
      setHistory(hist);
    } catch (err) {
      console.error('Error loading wallet data:', err);
      showMessage('Failed to load wallet data.', true);
    } finally {
      setLoading(false);
    }
  }, [currentCustomer, salonId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshPendingAndHistory();
  }, [refreshPendingAndHistory]);

  // Generates a PayNow reference + QR for the chosen package. Deliberately
  // stateless on the backend — no Firestore transaction document exists yet.
  // One is only ever created once the customer actually uploads their
  // payment proof (see handleUploadProof), so an abandoned attempt never
  // touches the transactions collection at all.
  const handleSelectPackage = async (pkg) => {
    if (!currentCustomer || pendingTopup || startingAmount) return;
    const amount = parseFloat(pkg.payAmount);
    setError('');
    setStartingAmount(amount);
    setModalGenerating(true);
    setModalOpen(true);
    setQrState(null);
    try {
      const refResponse = await fetch(`${API_BASE_URL}/api/wallet/generate-reference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salonId, amount })
      });
      const refData = await refResponse.json();
      if (!refData.success) throw new Error(refData.message || 'Failed to generate reference');

      const qrResponse = await fetch(`${API_BASE_URL}/api/wallet/generate-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salonId, amount, reference: refData.reference })
      });
      const qrData = await qrResponse.json();
      if (!qrData.success) throw new Error(qrData.message || 'Failed to generate QR code');

      setQrState({
        qrCodeData: qrData.qrCodeData,
        qrCodeUrl: qrData.qrCodeImageUrl,
        fallback: qrData.fallback,
        isDynamic: qrData.isDynamic,
        amount,
        // Server-confirmed credit amount for this pay amount (authoritative
        // over the locally-fetched package list, in case it changed).
        creditAmount: refData.creditAmount != null ? parseFloat(refData.creditAmount) : parseFloat(pkg.creditAmount),
        reference: refData.reference
      });
    } catch (err) {
      console.error('Error starting top-up:', err);
      setModalOpen(false);
      showMessage('Failed to start top-up. Please try again.', true);
    } finally {
      setModalGenerating(false);
      setStartingAmount(null);
    }
  };

  // Only now — once the customer has actually paid and is uploading proof —
  // does a wallet_topup document get written to Firestore at all.
  const handleUploadProof = async (file) => {
    await createTopupTransactionWithProof({
      salonId,
      customerId,
      customerEmail: currentCustomer.email,
      customerName: currentCustomer.displayName || currentCustomer.email,
      amount: qrState.amount,
      creditAmount: qrState.creditAmount,
      reference: qrState.reference,
      qrType: qrState.isDynamic ? 'dynamic' : 'static_fallback',
      file
    });
    await refreshPendingAndHistory();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setQrState(null);
    refreshPendingAndHistory();
  };

  const historyStatus = (t) => {
    if (t.walletTopupStatus === 'verified') return { label: 'Verified', cls: 'verified' };
    if (t.walletTopupStatus === 'rejected') return { label: 'Rejected', cls: 'rejected' };
    return { label: 'Pending Review', cls: 'pending' };
  };

  if (loading) {
    return (
      <div className="ap-loyalty-container loyalty-program">
        <div className="skel-line skel-line--md ap-skel-title" />
        <div className="skel-hero-bar" style={{ height: 168, borderRadius: 22 }} />
        <div className="skel-section" style={{ borderRadius: 18 }} />
      </div>
    );
  }

  return (
    <div className="ap-loyalty-container loyalty-program">
      <main className="ap-loyalty-main">

        <div className="ap-page-header">
          <h1 className="ap-page-title">Wallet</h1>
        </div>

        {(error || successMessage) && (
          <div className="ap-toast-container">
            {error && (
              <div className="ap-toast ap-toast--error">
                <span>{error}</span>
                <button onClick={() => setError('')} aria-label="Dismiss">×</button>
              </div>
            )}
            {successMessage && (
              <div className="ap-toast ap-toast--success">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage('')} aria-label="Dismiss">×</button>
              </div>
            )}
          </div>
        )}

        {/* ── Hero balance card ── */}
        <div className="ap-hero-card" ref={balanceRef} id="section-balance">
          <div className="ap-hero-eyebrow">
            <span className="ap-hero-tier-badge">Wallet Balance</span>
          </div>
          <div className="ap-hero-points-row">
            <span className="ap-hero-pts-num">{formatCurrency(balance)}</span>
          </div>
          <div className="ap-hero-top-tier">Available to spend on services, products, and in-salon purchases</div>
        </div>

        {/* ── Cash Packages ── */}
        <section className="ap-section" ref={topupRef} id="section-topup">
          <h2 className="ap-section-title">Cash Top-Up</h2>

          {pendingTopup ? (
            <div className="wallet-pending-card">
              <ClockIcon />
              <p>
                Your {formatCurrency(pendingTopup.amount)} cash top-up
                {pendingTopup.creditAmount > pendingTopup.amount && ` (credits ${formatCurrency(pendingTopup.creditAmount)})`}{' '}
                is awaiting verification. We'll notify you once it's reviewed.
              </p>
            </div>
          ) : (
            <div className="wallet-amount-grid">
              {packages.map(pkg => {
                const payAmount = parseFloat(pkg.payAmount);
                const creditAmount = parseFloat(pkg.creditAmount);
                const hasBonus = creditAmount > payAmount;
                return (
                  <button
                    key={payAmount}
                    className="wallet-amount-btn"
                    onClick={() => handleSelectPackage(pkg)}
                    disabled={!!startingAmount || !currentCustomer}
                  >
                    {startingAmount === payAmount ? 'Starting...' : (
                      <>
                        <span className="wallet-amount-btn-pay">{formatCurrency(payAmount)}</span>
                        {hasBonus && (
                          <span className="wallet-amount-btn-credit">Get {formatCurrency(creditAmount)}</span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Cash Top-Up History ── */}
        <section className="ap-section">
          <h2 className="ap-section-title">Cash Top-Up History</h2>
          {history.length === 0 ? (
            <div className="ap-empty-state">
              <WalletIcon />
              <p className="ap-empty-title">No cash top-ups yet</p>
              <p className="ap-empty-sub">Your cash top-up requests will appear here.</p>
            </div>
          ) : (
            <div className="ap-history-list">
              {history.map(t => {
                const status = historyStatus(t);
                return (
                  <div key={t.id} className="ap-history-item">
                    <div className="ap-history-icon">
                      {status.cls === 'verified' ? <CheckCircleIcon /> : <ClockIcon />}
                    </div>
                    <div className="ap-history-body">
                      <div className="ap-history-name">
                        {formatCurrency(t.amount)} Cash Top-Up
                        {t.creditAmount > t.amount && ` → ${formatCurrency(t.creditAmount)} credit`}
                      </div>
                      <div className="ap-history-meta">
                        <span className="ap-history-date">{formatDate(t.createdAt)}</span>
                      </div>
                    </div>
                    <div className={`wallet-status-tag wallet-status-tag--${status.cls}`}>{status.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </main>

      <WalletTopupQRModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        qrCodeData={qrState?.qrCodeData}
        qrCodeUrl={qrState?.qrCodeUrl}
        fallback={qrState?.fallback}
        amount={qrState?.amount}
        isGenerating={modalGenerating}
        onUploadProof={handleUploadProof}
      />
    </div>
  );
}

export default Wallet;
